import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container, Typography, Box, Alert, Paper,
  Divider, CircularProgress, Table, TableBody,
  TableCell, TableRow,
} from '@mui/material';
import AttendanceChart from './AttendanceChart.jsx';
import EmployeeTable from './Employeetable.jsx';
import axios from 'axios';

const ReportView = () => {
  const { reportId } = useParams();
  const [presentEmployees, setPresentEmployees] = useState([]);
  const [employees, setEmployees]               = useState([]);
  const [reportDate, setReportDate]             = useState('');
  const [vehicle, setVehicle]                   = useState({});
  const [location, setLocation]                 = useState('');
  const [error, setError]                       = useState('');
  const [loading, setLoading]                   = useState(true);

  useEffect(() => {
    const fetchReportData = async () => {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://attend.zventures.in';
      try {
        const res = await axios.get(`${backendUrl}/api/get-report/${reportId}`);
        const d = res.data;
        setPresentEmployees(d.presentEmployees || []);
        setReportDate(d.date || '');
        setVehicle(d.vehicle || {});
        setEmployees(d.employees || []);
        setLocation(d.location || '');
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch report');
      } finally {
        setLoading(false);
      }
    };
    fetchReportData();
  }, [reportId]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="subtitle1" mt={2}>Loading Report...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const totalRegistered  = employees.length;
  const usingTransport   = presentEmployees.length;
  const notUsing         = totalRegistered - usingTransport;
  const usagePct         = totalRegistered > 0
    ? ((usingTransport / totalRegistered) * 100).toFixed(1)
    : '0.0';

  const summaryRows = [
    { label: '✅ Present', value: usingTransport,   bg: '#e8f5e9', color: '#2e7d32' },
    { label: '❌ Absent', value: notUsing,      bg: '#ffebee', color: '#c62828' },
    { label: '👥 Total Registered',            value: totalRegistered, bg: '#e3f2fd', color: '#1565c0' },
    { label: '📊 Transport Usage Rate',        value: `${usagePct}%`, bg: '#f3e5f5', color: '#6a1b9a' },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      <Paper elevation={6} sx={{ p: 4, borderRadius: 4, background: 'linear-gradient(to right, #f5f7fa, #e2ecf5)' }}>

        {/* ── Header ── */}
        <Box mb={3} textAlign="center">
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0d47a1', mb: 1, textTransform: 'uppercase' }}>
            {location} Report
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" color="text.secondary">📅 Date: {reportDate}</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            🚌 Vehicle: {vehicle.name} ({vehicle.capacity} seater, {vehicle.plate})
          </Typography>
        </Box>

        {/* ── Attendance Chart heading ── */}
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1565c0', mb: 2 }}>
          Attendance Chart
        </Typography>

        {/* ── Chart (left) + Summary table (right) side by side ── */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
            alignItems: 'flex-start',
            mb: 5,
          }}
        >
          {/* Chart */}
          <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: '50%' } }}>
            <AttendanceChart
              employees={employees}
              presentEmployees={presentEmployees}
              vehicle={vehicle}
            />
          </Box>

          {/* Summary table */}
          <Box sx={{ flex: 1, alignSelf: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#37474f', mb: 2 }}>
              Summary
            </Typography>
            <Table size="small" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <TableBody>
                {summaryRows.map(row => (
                  <TableRow key={row.label}>
                    <TableCell
                      sx={{
                        background: row.bg,
                        color: row.color,
                        fontWeight: 'bold',
                        border: '1px solid #e0e0e0',
                        fontSize: '0.95rem',
                        py: 1.5,
                        px: 2,
                      }}
                    >
                      {row.label}
                    </TableCell>
                    <TableCell
                      sx={{
                        background: row.bg,
                        color: row.color,
                        fontWeight: 'bold',
                        border: '1px solid #e0e0e0',
                        fontSize: '1.1rem',
                        py: 1.5,
                        px: 2,
                        textAlign: 'center',
                        minWidth: 80,
                      }}
                    >
                      {row.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* ── Full employee detail table ── */}
        <Divider sx={{ mb: 3 }} />
        <EmployeeTable employees={employees} presentEmployees={presentEmployees} />

      </Paper>
    </Container>
  );
};

export default ReportView;